import { useDashboardIdentity } from './useDashboardIdentity';
import { useQuery } from '@tanstack/react-query';
import type { OperatorDashboardData, OperatorDashboardPeriod } from '@evi/shared';
import { fetchOperatorSummary } from '@/lib/api';

/**
 * React-Query-backed hook for the operator dashboard payload.
 *
 * `period` selects the revenue series window ('24h' | '7d' | '30d').
 * The query key includes `period` so caches for different windows
 * don't collide — flipping the dashboard's period chip lands an
 * already-fetched window instantly if it was selected before, and
 * stays fresh independently per window.
 *
 * `staleTime: 30_000` prevents refetches on every remount (e.g. when
 * a widget is toggled in the grid).
 *
 * Live (Warren, 28 Oct 2026): session and charger events refetch the
 * tiles through LiveUpdatesProvider as soon as a session starts,
 * meters, completes or a charger flips, so there is no poll.
 */
export function useOperatorSummary(period: OperatorDashboardPeriod = '24h') {
  const identity = useDashboardIdentity();
  return useQuery<OperatorDashboardData>({
    queryKey: ['dashboard', identity, 'operator-summary', period],
    queryFn: () => fetchOperatorSummary(period),
    staleTime: 30_000,
  });
}
